/**
 * Pub 1220 Type of Return codes and the amount-code tables, in one place.
 *
 * The codes are read off IRS Publication 1220 (Rev. 5-2026, Tax Year 2025), Part C. Sec. 2,
 * "Issuer \"A\" Record - Amount Codes". The columns each code lands in are core-api's, cross
 * checked against each form's interface and generator - a code that maps to a column no
 * generator reads is a value that silently disappears, so every entry below was checked
 * against `forms/{form}/gen-xml.ts`.
 *
 * Where core-api and the publication disagreed, the publication wins. Those places are marked
 * PUB 1220 below.
 */
import type { FormType } from "../context";

/**
 * The amount-code ordinal sequence. The B record carries eighteen payment amounts, and
 * PaymentAmount N holds the amount for the code at index N-1 here. Pub 1220 skips the letter
 * I (it reads as a digit), which is why the sequence runs ...G, H, J.
 */
export const AMOUNT_CODE_SEQUENCE = "123456789ABCDEFGHJ".split("");

/** Amount code for a 1-based PaymentAmount slot, or "" when out of range. */
export function amountCodeForSlot(slot: number): string {
  return AMOUNT_CODE_SEQUENCE[slot - 1] ?? "";
}

/**
 * Type of Return code -> canonical form. Inverted from core-api's typeOfReturnMap, with one
 * addition: 1099-B is "B" per Pub 1220 (core-api has no Pub 1220 path for 1099-B, so it is
 * absent from that map).
 *
 * EXTRACTION NOTE: "A" is 1099-MISC and "NE" is 1099-NEC. Before tax year 2020 the NEC boxes
 * lived on the MISC form and "A" covered both; a reader that maps "A" to NEC is reading a
 * pre-2020 assumption. 1099-SA is "M" and 5498-SA is "K", not the numeric codes they are
 * sometimes quoted as.
 */
export const TYPE_OF_RETURN_TO_FORM: Record<string, FormType> = {
  A: "1099-MISC", // Miscellaneous Information
  NE: "1099-NEC", // Nonemployee Compensation (its own return since TY2020)
  "6": "1099-INT", // Interest Income
  "1": "1099-DIV", // Dividends and Distributions
  "9": "1099-R", // Distributions From Pensions, Annuities, Retirement Plans
  MC: "1099-K", // Payment Card and Third Party Network Transactions
  M: "1099-SA", // Distributions From an HSA, Archer MSA, or Medicare Advantage MSA
  "5": "1099-C", // Cancellation of Debt
  T: "1099-LTC", // Long-Term Care and Accelerated Death Benefits
  B: "1099-B", // Proceeds From Broker and Barter Exchange Transactions
  "2": "1098-E", // Student Loan Interest Statement
  "8": "1098-T", // Tuition Statement
  K: "5498-SA", // HSA, Archer MSA, or Medicare Advantage MSA Information
  N: "3921", // Exercise of an Incentive Stock Option
  Z: "3922", // Transfer of Stock Acquired Through an Employee Stock Purchase Plan
  // PUB 1220: core-api has no 1099-Q path; the code is read off Part C, Sec. 2.
  Q: "1099-Q", // Payments From Qualified Education Programs (Under Sections 529 and 530)
};

/** Canonical form -> Type of Return code, for writing a file back out. */
export const FORM_TO_TYPE_OF_RETURN: Record<string, string> = Object.entries(
  TYPE_OF_RETURN_TO_FORM
).reduce<Record<string, string>>((acc, [code, form]) => {
  if (!acc[form]) acc[form] = code;
  return acc;
}, {});

/** Amount codes each form declares in its A record. Copied from core-api's amountCodesMap. */
export const FORM_AMOUNT_CODES: Record<string, string> = {
  // PUB 1220: no code B. Golden parachute payments left the FIRE system for TY2025 and
  // must be filed through IRIS or on paper. G is prior-year NEC (TY2019 and earlier).
  "1099-MISC": "1234568ACDEFG",
  "1099-NEC": "14",
  "1099-INT": "12345689ABDE",
  "1098-T": "13457",
  "1098-E": "1",
  "1099-DIV": "12356789ABCDEFGHJ",
  "1099-R": "12345689AB",
  "1099-C": "237",
  "1099-LTC": "12",
  "1099-SA": "124",
  "5498-SA": "12345",
  "1099-K": "12456789ABCDEFG",
  "3921": "34",
  "3922": "3458",
  // PUB 1220: 1099-B has an amount table after all - core-api simply never wrote one.
  "1099-B": "234579ABCD",
  // PUB 1220: gross distribution, earnings (or loss), basis.
  "1099-Q": "123",
};

/**
 * Amount code -> canonical record column, per form.
 *
 * RESOLVED against Pub 1220: core-api's two tables disagreed on 1099-MISC code E.
 * `eFileStructureUtils.ts` sent it to BOX15_STATE_TAX_WITHHELD2, `efile.Crecord.ts` left it
 * unmapped and put nonqualified compensation on code G. The publication settles it - code E is
 * Section 409A income, which is the nonqualified deferred compensation box, and code G is
 * prior-year NEC from TY2019 and earlier, which no current 1099-MISC has a box for. Neither
 * core-api table was right.
 *
 * PUB 1220 NOTE: 1099-NEC code 1 maps to BOX1A_NONEMPLOYEE_COMPENSATION, the 2026 column name.
 * core-api's tables still say BOX1_NONEMPLOYEE_COMPENSATION; the generator reads BOX1A first
 * and falls back to BOX1 (see forms/1099-NEC/draft-v2-deferred.ts), so the newer name is the
 * one to produce.
 *
 * PUB 1220 NOTE: 1042-S is not on this list because it is not a Publication 1220 form at all.
 * Its specification is Publication 1187 and it uses a "Q" record, not a "B" record, so a FIRE
 * 1042-S file cannot be read by this parser and is reported as such rather than half-read.
 */
export const AMOUNT_COLUMNS_BY_FORM: Record<string, Record<string, string>> = {
  "1099-NEC": {
    "1": "BOX1A_NONEMPLOYEE_COMPENSATION",
    "4": "BOX4_FED_INCOME_TAX_WITHHELD",
  },
  "1099-MISC": {
    "1": "BOX1_RENTS",
    "2": "BOX2_ROYALTIES",
    "3": "BOX3_OTHER_INCOME",
    "4": "BOX4_FED_INCOME_TAX_WITHHELD",
    "5": "BOX5_FISHING_BOAT_PROCEEDS",
    "6": "BOX6_MEDICAL_HEALTHCARE_PAYMENTS",
    "8": "BOX8_SUBSTITUTE_PAYMENTS",
    A: "BOX9_CROP_INSURANCE_PROCEEDS",
    // Retired from FIRE for TY2025; kept so a prior-year file still reads.
    B: "BOX14_GOLDEN_PARACHUTE",
    C: "BOX10_GROSS_PROCEEDS_ATTORNEY",
    D: "BOX12_SECTION_409A_DEFERRAL",
    F: "BOX11_FISH_PURCHASED_RESALE",
    // PUB 1220: E is Section 409A income, which is the nonqualified deferred compensation
    // box on the form. Code G is prior-year NEC and has no box on a current 1099-MISC.
    E: "BOX15_NONQUALIFIED_COMPENSATION",
  },
  "1099-INT": {
    "1": "BOX1_INTEREST_INCOME_NOT_IN_BOX3",
    "2": "BOX2_EARLY_WITHDRAWAL_PENALTY",
    "3": "BOX3_INTEREST_ON_US_SAVINGS",
    "4": "BOX4_FED_INCOME_TAX_WITHHELD",
    "5": "BOX5_INVESTMENT_EXPENSES",
    "6": "BOX6_FOREIGN_TAX_PAID",
    "8": "BOX8_TAX_EXEMPT_INTEREST",
    "9": "BOX9_SPEC_PRIVATE_ACT_BOND_INT",
    A: "BOX10_MARKET_DISCOUNT_BOND",
    B: "BOX11_BOND_PREMIUM",
    D: "BOX12_BOND_PREMIUM_ON_TREASURY",
    E: "BOX13_TAX_EXEMPT_BOND",
  },
  "1099-DIV": {
    "1": "BOX1A_TOTAL_ORDINARY_DIVIDENDS",
    "2": "BOX1B_QUALIFIED_DIVIDENDS",
    "3": "BOX2A_TOTAL_CAPITAL_GAIN_DISTR",
    "5": "BOX5_SECTION_199A_DIVIDENDS",
    "6": "BOX2B_UNRECAP_SEC_1250_GAIN",
    "7": "BOX2C_SECTION_1202_GAIN",
    "8": "BOX2D_COLLECTIBLES_GAIN",
    "9": "BOX3_NONDIVIDEND_DISTRIBUTIONS",
    A: "BOX4_FEDERAL_INCOME_TAX_WITHHELD",
    B: "BOX6_INVESTMENT_EXPENSES",
    C: "BOX7_FOREIGN_TAX_PAID",
    D: "BOX9_CASH_LIQUIDATION_DISTRIBUTIONS",
    E: "BOX10_NONCASH_LIQUIDATION_DISTRIBUTIONS",
    // PUB 1220: exempt interest dividends is box 12, and core-api's "BOX11_" name is a
    // column no 1099-DIV generator reads, so these amounts used to vanish on the way in.
    F: "BOX12_EXEMPT_INTEREST_DIVIDENDS",
    G: "BOX13_SPECIFIED_PRIVATE_ACTIVITY_BOND",
    H: "BOX2E_SECTION_897_ORD_DIV",
    J: "BOX2F_SECTION_897_CAP_GAIN",
  },
  "1099-R": {
    "1": "BOX1_GROSS_DISTRIBUTION",
    "2": "BOX2A_TAXABLE_AMOUNT",
    "3": "BOX3_CAPITAL_GAIN",
    "4": "BOX4_FEDERAL_INCOME_TAX_WITHHELD",
    "5": "BOX5_EMPLOYEE_CONTRIBUTIONS",
    "6": "BOX6_UNREALIZED_APPRECIATION",
    "8": "BOX8_OTHER",
    "9": "BOX9B_TOT_EMPLOYEE_CONTRIBUTIONS",
    // Code A, the IRA/SEP/SIMPLE distribution amount, has no box of its own: core-api writes
    // it from box 1 when it produces a 1220 file. Reading it back into box 1 overwrote the
    // gross distribution (code 1, an earlier slot) whenever the two differed, so it is left
    // unmapped and carried as PUB1220_AMOUNT_A. The IRA/SEP/SIMPLE checkbox comes from its
    // own indicator at position 547.
    B: "BOX10_AMOUNT_ALLOCABLE",
  },
  "1099-K": {
    "1": "BOX1A_GROSS_AMT_PAYMENT_CARD_TPN_TRANS",
    "2": "BOX1B_CARD_NOT_PRESENT_TRANS",
    "4": "BOX4_FED_INCOME_TAX_WTHLD",
    "5": "BOX5A_JANUARY",
    "6": "BOX5B_FEBRUARY",
    "7": "BOX5C_MARCH",
    "8": "BOX5D_APRIL",
    "9": "BOX5E_MAY",
    A: "BOX5F_JUNE",
    B: "BOX5G_JULY",
    C: "BOX5H_AUGUST",
    D: "BOX5I_SEPTEMBER",
    E: "BOX5J_OCTOBER",
    F: "BOX5K_NOVEMBER",
    G: "BOX5L_DECEMBER",
  },
  "1099-SA": {
    "1": "BOX1_GROSS_DISTRIBUTION",
    "2": "BOX2_EARNING_ON_EXCESS",
    "4": "BOX4_FMV_ON_DATE_OF_DEATH",
  },
  "1099-C": {
    "2": "BOX2_AMT_OF_DEBT_DISCHARGED",
    "3": "BOX3_INTEREST_INCL_IN_BOX2",
    "7": "BOX7_FAIR_MARKET_VALUE",
  },
  "1099-LTC": {
    "1": "BOX1_GROSS_BENEFITS_PAID",
    "2": "BOX2_ACCELERATED_DEATH_BENEFITS_PAID",
  },
  "1098-E": {
    "1": "BOX1_STUDENT_LOAN_INTEREST",
  },
  "1098-T": {
    "1": "BOX1_PAYMENTS_RECEIVED",
    "3": "BOX4_ADJ_MADE_PRIOR_YR",
    "4": "BOX5_SCHOLARSHIP_GRANTS",
    "5": "BOX6_ADJ_SCHOLARSHIP_PRIOR_YR",
    "7": "BOX10_REIMBURSEMENT_OR_REFUND",
  },
  "5498-SA": {
    "1": "BOX1_ARCHER_MSA_CONTRIBUTIONS",
    "2": "BOX2_TOTAL_CONTRIBUTIONS",
    "3": "BOX3_TOTAL_HSA_ARCHER_CONTRIBUTIONS",
    "4": "BOX4_ROLLOVER_CONTRIBUTIONS",
    "5": "BOX5_FAIR_MARKET_VALUE_HSA_ARCHER_MSA_MA_MSA",
  },
  "3921": {
    "3": "BOX3_EXE_PRICE_PER_SHARE",
    "4": "BOX4_FAIR_MARKET_VALUE_ON_SHARE",
  },
  "3922": {
    "3": "BOX3_FAIR_MARKET_VALUE_GRANT_DATE",
    "4": "BOX4_FAIR_MARKET_VALUE_EXE_DATE",
    "5": "BOX5_EXERCISE_PRICE_PAID",
    "8": "BOX8_EXERCISE_PRICE_PAID_ON_GRANT_DATE",
  },
  // PUB 1220 Part C. Sec. 2. core-api has no 1099-B amount table at all; this one is read
  // off the publication and checked against the Record1099B interface.
  "1099-B": {
    "2": "BOX_1D_PROCEEDS",
    "3": "BOX_1E_COST_BASIS",
    "4": "BOX_4_FEDERAL_TAX_WITHHELD",
    "5": "BOX_1G_WASH_SALE_LOSS",
    "7": "BOX_13_BARTERING",
    // Boxes 8 to 11 are the regulated futures and foreign currency contract figures. The
    // interface names still carry the years they were introduced with.
    "9": "BOX_8_PROFIT_LOSS_2019",
    A: "BOX_9_UNREALIZED_PL_2018",
    B: "BOX_10_UNREALIZED_PL_2019",
    C: "BOX_11_AGGREGATE_PL",
    D: "BOX_1F_MARKET_DISCOUNT",
  },
  // PUB 1220 Part C. Sec. 2. Code 2 is earnings or a loss, the one 1099-Q amount that may be
  // negative.
  "1099-Q": {
    "1": "BOX1_GROSS_DISTRIBUTION",
    "2": "BOX2_EARNINGS",
    "3": "BOX3_BASIS",
  },
};

/** The canonical column for an amount code, or a PUB1220_AMOUNT_ carrier so nothing is lost. */
export function amountColumn(form: string, code: string): string {
  return AMOUNT_COLUMNS_BY_FORM[form]?.[code] ?? `PUB1220_AMOUNT_${code}`;
}
